import React, { useMemo } from "react";
import { useTranslation } from "@helpers/translate";
import "./index.sass";

const CALENDAR_MODES = {
  DAILY: "daily",
  MONTHLY: "monthly",
};

const WEEK_DAY_KEYS = [
  "common.weekdays.monday",
  "common.weekdays.tuesday",
  "common.weekdays.wednesday",
  "common.weekdays.thursday",
  "common.weekdays.friday",
  "common.weekdays.saturday",
  "common.weekdays.sunday",
];

const MONTH_KEYS = [
  "common.months.january",
  "common.months.february",
  "common.months.march",
  "common.months.april",
  "common.months.may",
  "common.months.june",
  "common.months.july",
  "common.months.august",
  "common.months.september",
  "common.months.october",
  "common.months.november",
  "common.months.december",
];

const getTodayDateKey = () => {
  const today = new Date();

  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(today.getDate()).padStart(2, "0")}`;
};

const formatPnl = (value) => {
  const number = Number(value || 0);

  const formattedValue = Math.abs(number).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (number < 0) return `-$${formattedValue}`;
  if (number > 0) return `+$${formattedValue}`;

  return "0.00";
};

const getCellModifier = (value) => {
  if (value > 0) return "is-positive";
  if (value < 0) return "is-negative";

  return "is-neutral";
};

const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();

const getMonthStartOffset = (year, month) => {
  const day = new Date(year, month - 1, 1).getDay();

  return day === 0 ? 6 : day - 1;
};

export const TradingPnlCalendar = ({
  mode = CALENDAR_MODES.DAILY,
  year,
  month,
  data,
  error,
  onModeChange,
  onYearChange,
  onMonthChange,
}) => {
  const { __ } = useTranslation();

  const todayDateKey = useMemo(() => getTodayDateKey(), []);
  const currentDate = useMemo(() => new Date(), []);

  const daysData = data?.days || [];
  const monthsData = data?.months || [];

  const weekDays = useMemo(() => WEEK_DAY_KEYS.map((key) => __(key)), [__]);

  const monthNames = useMemo(() => MONTH_KEYS.map((key) => __(key)), [__]);

  const daysByDate = useMemo(() => {
    return daysData.reduce((acc, item) => {
      acc[item.date] = item.pnl;

      return acc;
    }, {});
  }, [daysData]);

  const monthsByNumber = useMemo(() => {
    return monthsData.reduce((acc, item) => {
      acc[item.month] = item.pnl;

      return acc;
    }, {});
  }, [monthsData]);

  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(year, month);
    const startOffset = getMonthStartOffset(year, month);
    const days = [];

    for (let i = 0; i < startOffset; i += 1) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = `${year}-${String(month).padStart(2, "0")}-${String(
        day,
      ).padStart(2, "0")}`;

      days.push({
        day,
        date,
        pnl: daysByDate[date],
        isToday: date === todayDateKey,
        isFuture: date > todayDateKey,
      });
    }

    return days;
  }, [year, month, daysByDate, todayDateKey]);

  const handlePrevPeriod = () => {
    if (mode === CALENDAR_MODES.MONTHLY) {
      onYearChange(year - 1);
      return;
    }

    if (month === 1) {
      onYearChange(year - 1);
      onMonthChange(12);
      return;
    }

    onMonthChange(month - 1);
  };

  const handleNextPeriod = () => {
    if (mode === CALENDAR_MODES.MONTHLY) {
      onYearChange(year + 1);
      return;
    }

    if (month === 12) {
      onYearChange(year + 1);
      onMonthChange(1);
      return;
    }

    onMonthChange(month + 1);
  };

  const periodLabel =
    mode === CALENDAR_MODES.DAILY
      ? `${year}-${String(month).padStart(2, "0")}`
      : year;

  return (
    <div className="trading-pnl-calendar">
      <div className="trading-pnl-calendar__header">
        <h3 className="trading-pnl-calendar__title">
          {__("trading_profile.trading_calendar")}
        </h3>

        <div className="trading-pnl-calendar__tabs">
          <button
            type="button"
            className={`trading-pnl-calendar__tab ${
              mode === CALENDAR_MODES.DAILY ? "is-active" : ""
            }`}
            onClick={() => onModeChange(CALENDAR_MODES.DAILY)}
          >
            {__("trading_profile.daily_pnl")}
          </button>

          <button
            type="button"
            className={`trading-pnl-calendar__tab ${
              mode === CALENDAR_MODES.MONTHLY ? "is-active" : ""
            }`}
            onClick={() => onModeChange(CALENDAR_MODES.MONTHLY)}
          >
            {__("trading_profile.monthly_pnl")}
          </button>
        </div>
      </div>

      <div className="trading-pnl-calendar__toolbar">
        <span className="trading-pnl-calendar__period-title">
          {mode === CALENDAR_MODES.DAILY
            ? `${monthNames[month - 1]} ${year}`
            : year}
        </span>

        <div className="trading-pnl-calendar__period-switcher">
          <button
            type="button"
            className="trading-pnl-calendar__period-button"
            onClick={handlePrevPeriod}
          >
            ‹
          </button>

          <span className="trading-pnl-calendar__period-value">
            {periodLabel}
          </span>

          <button
            type="button"
            className="trading-pnl-calendar__period-button"
            onClick={handleNextPeriod}
          >
            ›
          </button>
        </div>
      </div>

      {error && (
        <div className="trading-pnl-calendar__message">
          {__("trading_profile.failed_load_calendar")}
        </div>
      )}

      {!error && mode === CALENDAR_MODES.DAILY && (
        <div className="trading-pnl-calendar__daily">
          <div className="trading-pnl-calendar__week-days">
            {weekDays.map((day) => (
              <span key={day} className="trading-pnl-calendar__week-day">
                {day}
              </span>
            ))}
          </div>

          <div className="trading-pnl-calendar__days-grid">
            {calendarDays.map((item, index) => {
              if (!item) {
                return (
                  <div
                    key={`empty-${index}`}
                    className="trading-pnl-calendar__day is-empty"
                  />
                );
              }

              const modifier =
                typeof item.pnl === "number" ? getCellModifier(item.pnl) : "";

              return (
                <div
                  key={item.date}
                  className={`trading-pnl-calendar__day ${modifier} ${
                    item.isFuture ? "is-future" : ""
                  } ${item.isToday ? "is-today" : ""}`}
                >
                  <span className="trading-pnl-calendar__day-number">
                    {item.isToday ? __("common.today") : item.day}
                  </span>

                  {typeof item.pnl === "number" && (
                    <span className="trading-pnl-calendar__day-pnl">
                      {formatPnl(item.pnl)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!error && mode === CALENDAR_MODES.MONTHLY && (
        <div className="trading-pnl-calendar__months-grid">
          {monthNames.map((monthName, index) => {
            const monthNumber = index + 1;
            const pnl = monthsByNumber[monthNumber];
            const modifier = typeof pnl === "number" ? getCellModifier(pnl) : "";

            const isFutureMonth =
              year > currentDate.getFullYear() ||
              (year === currentDate.getFullYear() &&
                monthNumber > currentDate.getMonth() + 1);

            return (
              <div
                key={monthName}
                className={`trading-pnl-calendar__month ${modifier} ${
                  isFutureMonth ? "is-future" : ""
                }`}
              >
                <span className="trading-pnl-calendar__month-name">
                  {monthName}
                </span>

                {typeof pnl === "number" && (
                  <span className="trading-pnl-calendar__month-pnl">
                    {formatPnl(pnl)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TradingPnlCalendar;